import aclService from '../../services/AclService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import responseService from '../../services/ResponseService';
import utilService from '../../services/UtilService';
import questionReader from '../../store/readHelpers/QuestionReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import qtiConstants from '../../qti/Constants';
import questionSelectionService from '../../services/QuestionSelectionService';

const questionDisplayFooterSelector = {
    selectShouldShowQuestionOutcome(isReadOnly, outcomeVariables) {
        const { SHOW_PREVIEW_WITH_FEEDBACK } = sessionReader.printParamNames;

        if ((sessionReader.isContextPreviewAssessmentRun() && !sessionReader.getPrintParam(SHOW_PREVIEW_WITH_FEEDBACK, false))
            || !utilService.isObjectWithProps(outcomeVariables)) {
            return false;
        }

        if (sessionReader.isContextPreview() && isReadOnly) {
            return true;
        }

        if (sessionReader.isContextViewGradingQuestionDelivery()
            || sessionReader.isContextViewDelivery()
            || sessionReader.isContextViewDeliveryForAnnotationPdf()
        ) {
            return aclService.canSeeCorrectResponsesInDelivery();
        }

        return false;
    },

    selectShouldShowPopulateWithCorrectAnswer(question) {
        return questionReader.isGapMatchQuestion(question) || questionReader.isGraphicGapMatchQuestion(question);
    },

    selectShouldShowResetButton(question, isDocument) {
        if (!sessionReader.isInteractive()) {
            return false;
        }

        if (marketplacePropertiesService.isResetResponseButtonHidden()) {
            return false;
        }

        if (!sessionReader.isResettableContext()) {
            return false;
        }

        const isResettableQuestion = questionReader.isResettableQuestion(question);

        return isResettableQuestion || (!isDocument && responseService.getResettableInteractionIds(question).length > 0);
    },

    selectShouldShowMaxScore(question, isDocument, isForm, showQuestionOutcome) {
        if (showQuestionOutcome || isDocument || isForm || isNaN(parseFloat(question.maxScore))) {
            return false;
        }

        return settingsReader.isShowMaxMarksEnabled();
    },

    selectShouldShowTheLine(showMaxScore, showCheckAnswer, showQuestionOutcome, showResetButton, isLastQuestionInCurrentNestedSection) {
        if (questionSelectionService.isCandidateSelectedContentBlockEnabled()) {
            return false;
        }
        return showMaxScore || showCheckAnswer || showQuestionOutcome || showResetButton || isLastQuestionInCurrentNestedSection;
    },

    selectShouldShowInfoBox(outcomeVariables) {
        if (!outcomeVariables || !sessionReader.isContextReadinessCheck()) {
            return false;
        }

        const isAutoQuestionCorrectOrAnswered = [
            qtiConstants.autoFeedbackNames.CORRECT,
            qtiConstants.autoFeedbackNames.ANSWERED,
        ].includes(outcomeVariables.FEEDBACK?.value);

        return !isAutoQuestionCorrectOrAnswered;
    },

    selectShouldShowResetInPreviewButton(isReadOnly) {
        return sessionReader.isViewMediaScreen() && sessionReader.isContextPreview() && isReadOnly;
    },

    selectProps(state, ownProps) {
        const {
            ui,
            response,
            settings,
            result,
        } = state;

        const {
            question,
            isLastQuestionInCurrentNestedSection,
        } = ownProps;

        const questionContentRevisionId = question.contentRevisionId;

        const itemResult = response[questionContentRevisionId] || {};
        const correctResponsesForQuestion = settingsReader.showCorrectResponsesIfAvailable()
            ? state.correctResponses[questionContentRevisionId]
            : undefined;
        const isDocument = questionReader.isDocument(question);
        const isForm = questionReader.isForm(question);

        const isCodeCompiler = questionReader.isCompiledCodeEditor(question);
        const isReadOnly = questionReader.questionIsReadOnly(question, itemResult);
        const outcomeVariables = isDocument ? null : itemResult?.outcomeVariables;
        const correctResponses = (isReadOnly || isCodeCompiler) ? correctResponsesForQuestion : undefined;

        const showResetButton = this.selectShouldShowResetButton(question, isDocument);
        const isManuallyScored = utilService.isQuestionTypeManuallyScored(question.objectType);

        const showCheckAnswer = questionReader.shouldShowCheckAnswer(
            isReadOnly,
            isManuallyScored,
            isDocument,
            isForm,
            question.objectType,
            question.customFields
        );
        const showQuestionOutcome = this.selectShouldShowQuestionOutcome(isReadOnly, outcomeVariables);
        const showMaxScore = this.selectShouldShowMaxScore(question, isDocument, isForm, showQuestionOutcome);
        const showPopulateWithCorrectAnswer = this.selectShouldShowPopulateWithCorrectAnswer(question);
        const showTheLine = this.selectShouldShowTheLine(showMaxScore, showCheckAnswer,
            showQuestionOutcome, showResetButton, isLastQuestionInCurrentNestedSection);

        const questionResult = result?.questions?.[questionContentRevisionId] || null;
        const showInfoBox = this.selectShouldShowInfoBox(outcomeVariables);
        const showResetInPreviewButton = this.selectShouldShowResetInPreviewButton(isReadOnly);

        return {
            correctResponses,
            correctResponsesForQuestion,
            isManuallyScored,
            isReadOnly,
            outcomeVariables,
            questionResult,
            settingValues: settings.values,
            showCheckAnswer,
            showInfoBox,
            showMaxScore,
            showPopulateWithCorrectAnswer,
            showQuestionOutcome,
            showResetButton,
            showResetInPreviewButton,
            showTheLine,
            ui,
        };
    },
};

export default questionDisplayFooterSelector;
